/**
 * Tarif formateur d'une session.
 *
 * La rentabilité (onglet Facturation de la fiche session) facture la prestation
 * formateur au tarif stocké sur la session : `sessions.trainer_rate_day`.
 *
 * RÈGLE RETENUE : la fiche intervenant fait autorité, toujours. À chaque
 * ouverture de l'onglet, la session est réalignée sur le tarif de la fiche du
 * formateur, y compris si un montant avait été saisi à la main. Une fiche sans
 * tarif ramène la session à 0, signalé à l'écran.
 *
 * Conséquence assumée : revaloriser un formateur modifie la marge des sessions
 * passées à la prochaine consultation. C'est le comportement demandé — le tarif
 * affiché doit toujours être celui de la fiche, sans exception à expliquer.
 */

/**
 * Une session est en distanciel dès lors qu'elle porte un lien de connexion.
 * Même règle que la ligne « Location salle » de la rentabilité, qui neutralise
 * les frais de salle sur `meeting_link`.
 */
export const isRemoteSession = (session) => Boolean(session?.meeting_link);

/** Convertit en nombre exploitable ; null si la valeur est absente ou vide. */
const toAmount = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * Tarif de référence d'un intervenant pour une session donnée : `tarif_distanciel`
 * en classe virtuelle, `tarif_presentiel` sinon. On retombe sur l'autre tarif
 * quand celui attendu n'est pas renseigné — une fiche ne porte souvent qu'un des
 * deux, et un tarif approchant vaut mieux que le défaut générique.
 *
 * Renvoie null si la fiche ne porte aucun tarif : dans ce cas on ne touche à rien.
 */
export const resolveTrainerRate = (intervenant, session) => {
  if (!intervenant) return null;
  const presentiel = toAmount(intervenant.tarif_presentiel);
  const distanciel = toAmount(intervenant.tarif_distanciel);
  return isRemoteSession(session)
    ? (distanciel ?? presentiel)
    : (presentiel ?? distanciel);
};

/** Compare deux noms sans tenir compte des accents, de la casse ni de l'ordre. */
const normalizeName = (s) => (s || '')
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .toLowerCase().replace(/\s+/g, ' ').trim();

/**
 * Parmi les intervenants rattachés à une session, lequel est LE formateur ?
 *
 * Le seul critère est le nom porté par la session (`sessions.trainer`) — celui
 * que l'utilisateur voit sur la fiche et sur la ligne « Prestation formateur ».
 * On ignore délibérément `is_principal` : plusieurs liens d'une même session
 * peuvent porter ce drapeau, et s'y fier revenait à facturer le tarif d'un
 * intervenant qui n'anime pas la session.
 *
 * Aucune correspondance -> null. Pas de repli sur « le premier de la liste » :
 * mieux vaut ne reprendre aucun tarif que le tarif de quelqu'un d'autre.
 */
export const pickSessionTrainer = (list, session) => {
  if (!Array.isArray(list) || list.length === 0) return null;
  const cible = normalizeName(session?.trainer);
  if (!cible) return null;
  return list.find((i) => {
    const prenomNom = normalizeName(`${i.firstname || ''} ${i.lastname || ''}`);
    const nomPrenom = normalizeName(`${i.lastname || ''} ${i.firstname || ''}`);
    return prenomNom === cible || nomPrenom === cible;
  }) || null;
};

/**
 * Tarif à appliquer à la session pour un formateur donné.
 *
 * Toujours celui de sa fiche. Une fiche sans tarif renvoie 0 : la session n'est
 * pas laissée sur une ancienne valeur qui ne correspondrait plus à personne, et
 * l'écran signale que la fiche est à compléter.
 */
export const trainerRateToApply = (intervenant, session) => {
  const tarif = resolveTrainerRate(intervenant, session);
  return tarif === null ? 0 : tarif;
};

/** La fiche du formateur est-elle dépourvue de tarif exploitable ? */
export const hasNoRateOnFile = (intervenant, session) => (
  intervenant !== null && intervenant !== undefined && resolveTrainerRate(intervenant, session) === null
);
